import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import FormControl from "@mui/material/FormControl"
import MenuItem from "@mui/material/MenuItem"
import Pagination from "@mui/material/Pagination"
import Select from "@mui/material/Select"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { useQuery } from "@tanstack/react-query"
import debounce from "lodash/debounce"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"

import DataTableToolbar from "@components/data_table_toolbar"
import GridTable from "@components/grid-table"
import {
  AdminCustomerPaymentRow,
  PaymentFilterKey,
  getAdminCustomerPayments
} from "@domain/customer/payments"
import { getCustomers } from "@domain/customer/list"
import { useAppContext } from "@contexts/app_context"
import { formatDate } from "@utils/functions/formatDate"

type PaymentRow = {
  id: string
  customerId: string
  company: string
  owner: string
  planName: string
  renewalType: string
  endDate?: string
  refreshDate?: string
  latestAmountPaid?: number
  appStatus: string
  accountStatus: string
}

type CustomerOption = {
  label: string
  value: string
}

const planOptions = [
  { label: "Tum Planlar", value: "" },
  { label: "Free Trial", value: "FreeTrial" },
  { label: "Start", value: "Start" },
  { label: "Grow", value: "Grow" },
  { label: "Scale", value: "Scale" },
  { label: "Special", value: "Special" }
]

function toDateFilterValue(value: string, endOfDay = false) {
  if (!value) {
    return undefined
  }

  return endOfDay
    ? new Date(`${value}T23:59:59.999Z`).toISOString()
    : new Date(`${value}T00:00:00.000Z`).toISOString()
}

function toDisplayName(row: AdminCustomerPaymentRow) {
  const firstName = row.customerPerson?.firstName ?? ""
  const lastName = row.customerPerson?.lastName ?? ""
  const fullName = `${firstName} ${lastName}`.trim()

  return fullName || "-"
}

function buildPaymentRow(row: AdminCustomerPaymentRow): PaymentRow | null {
  const customerId = row.customer?._id
  if (!customerId) {
    return null
  }

  return {
    id: customerId,
    customerId,
    company: row.customer?.companyName ?? "-",
    owner: toDisplayName(row),
    planName: row.customer?.package?.name ?? "-",
    renewalType: row.customer?.package?.periodMode ?? "-",
    endDate: row.customer?.package?.endDate,
    refreshDate: row.customer?.package?.refreshDate,
    latestAmountPaid: row.latestInvoice?.amountPaid,
    appStatus: row.customer?.appStatus ?? "-",
    accountStatus: row.customer?.accountStatus ?? "-"
  }
}

const filterLabels: { key: PaymentFilterKey; label: string }[] = [
  { key: "ALL", label: "Tum Kayitlar" },
  { key: "UPCOMING_END_DATE", label: "Odemesi Yaklasanlar" },
  { key: "UPCOMING_REFRESH_DATE", label: "Paket Yenilemesi Yaklasanlar" },
  { key: "PAID_TODAY", label: "Bu Ay Odeme Yapanlar" },
  { key: "PAID_THIS_WEEK", label: "Bu Hafta Odeme Yapanlar" },
  { key: "INVOICE_INFO", label: "Fatura Bilgisi Olanlar" },
  { key: "PROBLEMATIC_ACCOUNTS", label: "Sorunlu Hesaplar" }
]

export default function CustomerPaymentsPage() {
  const { translate } = useAppContext()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [activeFilter, setActiveFilter] = useState<PaymentFilterKey>("ALL")
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerOption | null>(null)
  const [customerOptionSearch, setCustomerOptionSearch] = useState("")
  // The option list is a server round-trip; don't fire it on every keystroke.
  const setCustomerOptionSearchDebounced = useMemo(
    () => debounce(setCustomerOptionSearch, 400),
    []
  )
  useEffect(
    () => () => setCustomerOptionSearchDebounced.cancel(),
    [setCustomerOptionSearchDebounced]
  )
  const [selectedPlanName, setSelectedPlanName] = useState("")
  const [paymentDateStart, setPaymentDateStart] = useState("")
  const [paymentDateEnd, setPaymentDateEnd] = useState("")
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      page: 0
    }))
  }, [
    search,
    activeFilter,
    selectedCustomer?.value,
    selectedPlanName,
    paymentDateStart,
    paymentDateEnd
  ])

  const { data: customerOptions = [], isLoading: isLoadingCustomerOptions } =
    useQuery({
      queryKey: ["customer-payment-options", customerOptionSearch],
      queryFn: async () => {
        const response = await getCustomers({
          pageNumber: 1,
          pageSize: 100,
          search: customerOptionSearch || undefined
        })

        return (response.data.docs ?? [])
          .map((item) => ({
            label:
              item.customer?.companyName ||
              `${item.customerPerson?.firstName ?? ""} ${
                item.customerPerson?.lastName ?? ""
              }`.trim() ||
              item.customerPerson?.email ||
              "-",
            value: item.customer?._id || ""
          }))
          .filter((item) => item.value)
      }
    })

  const { data, isLoading } = useQuery({
    queryKey: [
      "customer-payments-page",
      search,
      activeFilter,
      selectedCustomer?.value,
      selectedPlanName,
      paymentDateStart,
      paymentDateEnd,
      pagination
    ],
    queryFn: async () => {
      const response = await getAdminCustomerPayments({
        search,
        customerId: selectedCustomer?.value,
        planName: selectedPlanName || undefined,
        paymentDateStart: toDateFilterValue(paymentDateStart),
        paymentDateEnd: toDateFilterValue(paymentDateEnd, true),
        filter: activeFilter,
        pageNumber: pagination.page + 1,
        pageSize: pagination.pageSize
      })

      if (!response.isSuccess) {
        throw new Error(String(response.errorMessage ?? "unknown"))
      }

      return response.data
    }
  })

  const rows = useMemo(() => {
    return (data?.docs ?? [])
      .map((item) => buildPaymentRow(item))
      .filter(Boolean) as PaymentRow[]
  }, [data])

  const totalPages = Math.max(
    1,
    Math.ceil((data?.totalDocs ?? 0) / pagination.pageSize)
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "company",
        minWidth: 180,
        headerName: translate("company"),
        renderCell: (params: GridRenderCellParams<PaymentRow>) => (
          <Box
            sx={{ cursor: "pointer", color: "#1976d2", fontWeight: 500 }}
            onClick={() => navigate(`/customers/${params.row.customerId}`)}
          >
            {params.value || "-"}
          </Box>
        )
      },
      {
        field: "owner",
        minWidth: 180,
        headerName: translate("owner")
      },
      {
        field: "planName",
        minWidth: 150,
        headerName: translate("main_plan")
      },
      {
        field: "renewalType",
        minWidth: 140,
        headerName: "Yenileme Tipi"
      },
      {
        field: "endDate",
        minWidth: 170,
        headerName: "Paket Bitis Tarihi",
        renderCell: (params: GridRenderCellParams<PaymentRow>) =>
          params.value
            ? formatDate(translate("date_format"), String(params.value))
            : "-"
      },
      {
        field: "refreshDate",
        minWidth: 170,
        headerName: "Yenileme Tarihi",
        renderCell: (params: GridRenderCellParams<PaymentRow>) =>
          params.value
            ? formatDate(translate("date_format"), String(params.value))
            : "-"
      },
      {
        field: "latestAmountPaid",
        minWidth: 140,
        headerName: "En Son Alinan Ucret",
        renderCell: (params: GridRenderCellParams<PaymentRow>) =>
          String(params.value ?? "-")
      },
      {
        field: "appStatus",
        minWidth: 120,
        headerName: "Uygulama Durumu"
      },
      {
        field: "accountStatus",
        minWidth: 120,
        headerName: "Hesap Durumu"
      }
    ],
    [navigate, translate]
  )

  return (
    <Stack sx={{ overflowY: "auto", height: "100%", p: 2, gap: 2 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Typography variant="h6">Musteri Odemeleri</Typography>
          <Typography variant="body2" color="text.secondary">
            customerProduct, fatura ve odeme verileri tek listede toplandi.
          </Typography>
        </Box>
        <DataTableToolbar searchText={search} onSearch={setSearch} />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} gap={2}>
        <Autocomplete<CustomerOption, false, false, false>
          sx={{ minWidth: 320 }}
          options={customerOptions}
          loading={isLoadingCustomerOptions}
          value={selectedCustomer}
          onChange={(_, value) => setSelectedCustomer(value || null)}
          onInputChange={(_, value, reason) => {
            if (reason === "input") {
              setCustomerOptionSearchDebounced(value)
            }
            if (reason === "clear") {
              setCustomerOptionSearchDebounced.cancel()
              setCustomerOptionSearch("")
            }
          }}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.label
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField {...params} label="Musteri" size="small" />
          )}
          clearOnEscape
        />
        <FormControl size="small" sx={{ minWidth: 320 }}>
          <Select
            value={activeFilter}
            onChange={(event) =>
              setActiveFilter(event.target.value as PaymentFilterKey)
            }
          >
            {filterLabels.map((filter) => (
              <MenuItem key={filter.key} value={filter.key}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            displayEmpty
            value={selectedPlanName}
            onChange={(event) =>
              setSelectedPlanName(String(event.target.value))
            }
          >
            {planOptions.map((plan) => (
              <MenuItem key={plan.value || "ALL_PLANS"} value={plan.value}>
                {plan.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Odeme Tarihi Baslangic"
          type="date"
          value={paymentDateStart}
          onChange={(event) => setPaymentDateStart(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 190 }}
        />
        <TextField
          size="small"
          label="Odeme Tarihi Bitis"
          type="date"
          value={paymentDateEnd}
          onChange={(event) => setPaymentDateEnd(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 190 }}
        />
        <Stack direction="row" alignItems="center">
          <Chip
            label={`${data?.totalDocs ?? 0} kayit`}
            color="primary"
            variant="outlined"
          />
        </Stack>
      </Stack>

      <GridTable
        sortable
        loading={isLoading}
        rows={rows}
        columns={columns}
        rowCount={data?.totalDocs ?? 0}
        paginationHidden
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        paginationMode="server"
        disableColumnSelector
      />

      <Stack direction="row" justifyContent="center" pb={2}>
        <Pagination
          count={totalPages}
          page={pagination.page + 1}
          color="primary"
          shape="rounded"
          onChange={(_, page) =>
            setPagination((prev) => ({
              ...prev,
              page: page - 1
            }))
          }
        />
      </Stack>
    </Stack>
  )
}
